// Pure, framework-free PlanReview linked-pair state machine. Extracted from
// PlanReview.jsx (behaviour-preserving) so the cook<->leftover interaction rules
// can be regression-tested deterministically. PlanReview delegates to these; the
// backend confirm/swap endpoints remain the final source of truth.

export var PRIMARY_SLOTS = ['Breakfast', 'Lunch', 'Dinner'];

export function normalizeSlot(mealType) {
  var s = (mealType || '').toString().trim();
  var map = {
    breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack',
    supper: 'Dinner', brunch: 'Lunch',
  };
  return map[s.toLowerCase()] || 'Snack';
}

// Merge-time guard: a single date must never keep two of the same PRIMARY slot.
// Keyed on SLOT (per date), NEVER on meal_id — so a legitimate duplicate recipe
// (e.g. a cook Dinner on day N and its leftover Lunch on day N+1, or the same
// recipe appearing on two different days) is never silently dropped.
export function dedupePrimarySlots(rows, date) {
  var seen = {};
  return (rows || []).filter(function (r) {
    if (r.date !== date) return true;
    var slot = normalizeSlot(r.meal_type);
    if (PRIMARY_SLOTS.indexOf(slot) === -1) return true;
    if (seen[slot]) return false;
    seen[slot] = true;
    return true;
  });
}

export function getGroupMembers(plans, groupId) {
  if (!groupId) return [];
  return (plans || []).filter(function (p) { return p.leftover_group_id === groupId; });
}

// A draft leftover group is VALID only when it is exactly one cook + one leftover
// of the SAME recipe, cook=Dinner, leftover=Lunch, equal positive consumption
// scales, on adjacent dates (leftover == cook + 1 day). Anything else is malformed
// and must be routed to the saved-plan Swap, not the draft pair-swap.
export function isValidDraftPair(plans, groupId) {
  var members = getGroupMembers(plans, groupId);
  if (members.length !== 2) return false;
  var cook = members.find(function (m) { return m.leftover_role === 'cook'; });
  var lo = members.find(function (m) { return m.leftover_role === 'leftover'; });
  if (!cook || !lo) return false;
  if (cook.meal_id !== lo.meal_id || !cook.meal_id) return false;
  var ns = function (s) { return (s || '').toString().trim().toLowerCase(); };
  if (ns(cook.meal_type) !== 'dinner') return false;
  if (ns(lo.meal_type) !== 'lunch') return false;
  var s1 = Number(cook.scale_factor || 0), s2 = Number(lo.scale_factor || 0);
  if (!(s1 > 0) || !(s2 > 0)) return false;
  if (Math.abs(s1 - s2) > 1e-3) return false;
  try {
    var cd = new Date(cook.date + 'T00:00:00');
    var ld = new Date(lo.date + 'T00:00:00');
    if (Math.round((ld - cd) / 86400000) !== 1) return false;
  } catch (e) { return false; }
  return true;
}

// The lock/remove UNIT for a row: a VALID linked pair returns BOTH members so any
// action (lock/unlock/remove) is applied pair-wide and can never leave an orphan.
// A regular or malformed-linked row returns just itself.
export function getPairMembers(plans, date, mealId) {
  var row = (plans || []).find(function (p) { return p.date === date && p.meal_id === mealId; });
  if (row && row.leftover_group_id && isValidDraftPair(plans, row.leftover_group_id)) {
    return getGroupMembers(plans, row.leftover_group_id);
  }
  return row ? [row] : [{ date: date, meal_id: mealId }];
}

export function lockKey(m) { return m.date + '_' + m.meal_id; }

export function isPairLocked(lockMap, members) {
  var map = lockMap || {};
  return (members || []).some(function (m) { return !!map[lockKey(m)]; });
}

export function applyLockToggle(prevLockMap, members, shouldLock) {
  var next = Object.assign({}, prevLockMap);
  (members || []).forEach(function (m) {
    var k = lockKey(m);
    if (shouldLock) { next[k] = true; } else { delete next[k]; }
  });
  return next;
}

// Remove pair-safe: a linked row drops its WHOLE group (never one-sided); a
// regular row drops only itself.
export function removePairFromPlans(plans, date, mealId) {
  var list = plans || [];
  var row = list.find(function (p) { return p.date === date && p.meal_id === mealId; });
  var gid = row && row.leftover_role ? row.leftover_group_id : null;
  if (gid) return list.filter(function (p) { return p.leftover_group_id !== gid; });
  return list.filter(function (p) { return !(p.date === date && p.meal_id === mealId); });
}

// Draft Replace Both: update BOTH linked rows to the same recipe, preserving each
// row's own consumption scale + role + shared group id, recomputing the cook's
// prep_scale_factor = S1 + S2. Never one-sided.
export function applyPairSwapToPlans(plans, groupId, alt) {
  var af = alt.scale_factor || 1.0;
  var baseCal = alt.calories / af, baseP = alt.protein / af, baseC = alt.carbs / af, baseF = alt.fat / af;
  var list = plans || [];
  var groupRows = list.filter(function (p) { return p.leftover_group_id === groupId; });
  var prep = groupRows.reduce(function (sum, p) { return sum + (p.scale_factor || 1.0); }, 0);
  return list.map(function (p) {
    if (p.leftover_group_id !== groupId) return p;
    var sf = p.scale_factor || 1.0;
    return Object.assign({}, p, {
      meal_id: alt.meal_id, meal_name: alt.meal_name,
      calories: Math.round(baseCal * sf), protein: Math.round(baseP * sf),
      carbs: Math.round(baseC * sf), fat: Math.round(baseF * sf),
      prep_scale_factor: p.leftover_role === 'cook' ? prep : p.prep_scale_factor,
      omitted_ingredient_ids: alt.omitted_ingredient_ids || [],
    });
  });
}

// Meal Review Swap identifies the EXACT draft row it replaces by a client-only
// key (two rows of one date may share a meal_id, e.g. two identical Snacks).
// Rows that already carry a key keep it. Never sent to Confirm (handleSave
// lists its fields explicitly).
var _draftRowSeq = 0;
export function withDraftRowKeys(rows) {
  return (rows || []).map(function (r) {
    if (!r || r.draft_row_key) return r;
    _draftRowSeq += 1;
    return Object.assign({}, r, { draft_row_key: 'dr_' + Date.now().toString(36) + '_' + _draftRowSeq });
  });
}

// One non-linked draft row replaced by a Swap choice ({meal_id, meal_name,
// calories, protein, carbs, fat, omitted_ingredient_ids} at 1.0x) -- the same
// fields the draft Swap has always set. Every other row is returned as is.
// A previously SAVED meal the user kept (is_kept + its saved plan_id) is no
// longer that saved row once swapped: /smart-fill/confirm never inserts an
// is_kept row and never deletes a row outside replace_plan_ids, so the
// replacement becomes a NEW row and remembers the saved row it replaces
// (`replaces_plan_id`, see draftReplacePlanIds). Logged food is left as is.
export function applySingleSwapToPlans(plans, rowKey, pick) {
  return (plans || []).map(function (p) {
    if (!rowKey || p.draft_row_key !== rowKey) return p;
    var fields = {
      meal_id: pick.meal_id, meal_name: pick.meal_name,
      calories: pick.calories, protein: pick.protein,
      carbs: pick.carbs, fat: pick.fat,
      scale_factor: 1.0, scale_label: null,
      omitted_ingredient_ids: pick.omitted_ingredient_ids || [],
    };
    if (p.is_kept && p.plan_id && !p.is_logged) {
      fields.is_kept = false;
      fields.plan_id = null;
      fields.replaces_plan_id = p.plan_id;
    }
    return Object.assign({}, p, fields);
  });
}

// The draft's replace_plan_ids: the generation's own list plus every kept
// saved row a Swap replaced (each id once).
export function draftReplacePlanIds(generatedIds, plans) {
  var out = (generatedIds || []).slice();
  (plans || []).forEach(function (p) {
    if (p && p.replaces_plan_id && out.indexOf(p.replaces_plan_id) === -1) out.push(p.replaces_plan_id);
  });
  return out;
}

// 6A-6C save gate: incomplete + legacy off_track are NOT saveable; on_track and
// constrained_no_feasible_addition ARE.
export function computeBlockedDates(complianceByDate, dates) {
  var map = complianceByDate || {};
  return (dates || []).filter(function (date) {
    var c = map[date];
    var st = c && c.status;
    return st === 'incomplete' || st === 'off_track';
  });
}

export function isSaveableStatus(status) {
  return status === 'on_track' || status === 'constrained_no_feasible_addition';
}
